/**
 * Invoice helpers
 *
 * Client-side mirror of the read-time "overdue" derivation added to the MCP
 * server (mcp-server/src/tools/invoiceTools.js, issue #278). The `status`
 * column on `invoices` never auto-transitions to `'overdue'` — there is no
 * cron job or trigger that does it — so overdue-ness must be derived from
 * `due_date` at read time instead of trusted from the stored column.
 *
 * Keep the exclusion list and comparison in sync with `deriveOverdue()` in
 * mcp-server/src/tools/invoiceTools.js and the `invoice_summary` view
 * (supabase/migrations/20260704120000_invoice_overdue_derivation.sql).
 *
 * @module utils/invoiceHelpers
 */

/**
 * Statuses for which "overdue" is not applicable:
 *  - paid / cancelled: terminal, no outstanding obligation
 *  - draft: not yet issued to the client, so no due obligation
 *  - overdue: already flagged
 */
export const OVERDUE_EXCLUDED_STATUSES = ['paid', 'cancelled', 'draft', 'overdue'];

/**
 * Whether an invoice is overdue right now: it has a due_date in the past
 * and is still in an active, unpaid state.
 * @param {{status?: string, due_date?: string}} invoice
 * @returns {boolean}
 */
export const isInvoiceOverdue = (invoice) => {
  if (!invoice || !invoice.due_date) return false;
  const today = new Date().toISOString().split('T')[0];
  return (
    !OVERDUE_EXCLUDED_STATUSES.includes(invoice.status) &&
    invoice.due_date < today
  );
};

/**
 * Effective status of an invoice, taking read-time overdue derivation into
 * account. Returns 'overdue' when the invoice is past due and unpaid,
 * otherwise the raw stored status.
 * @param {{status?: string, due_date?: string}} invoice
 * @returns {string}
 */
export const getEffectiveInvoiceStatus = (invoice) =>
  isInvoiceOverdue(invoice) ? 'overdue' : invoice?.status;

/**
 * Decorate an invoice with `is_overdue` and `effective_status`, mirroring
 * the shape returned by the MCP get_invoice / list_invoices tools.
 * @param {Object} invoice
 * @returns {Object} invoice with is_overdue/effective_status added
 */
export const withOverdueDerivation = (invoice) => ({
  ...invoice,
  is_overdue: isInvoiceOverdue(invoice),
  effective_status: getEffectiveInvoiceStatus(invoice),
});
